import {
  deleteEntities,
  getAllEntitiesApply,
  getEntity,
  selectAllEntities,
  selectEntity,
  updateEntities,
  upsertEntities,
} from '@ngneat/elf-entities'
import { Injectable } from '@angular/core'
import { PlanDao } from '../services/dao/plan.dao'
import { Plan } from '../models/plan'
import { planStore } from './plan-creation.repository'
import { NavStep } from '../models/navSteps'
import { Observable } from 'rxjs'
import {
  ApplicationInsightsService,
  ApplicationInsightsStates,
} from '../services/applicationInsights.service'
import { PlanService } from '../services/plan.service'
import { Doka360ProjectSelectorService } from '../services/doka360-project-selector.service'

@Injectable({
  providedIn: 'root',
})
export class PlanRepository {
  public readonly plans$ = planStore.pipe(selectAllEntities())

  private planOffset: number | null = null
  private planLimit = 10
  private totalPlanCount?: number

  public get hasMorePlans(): boolean {
    return this.totalPlanCount && this.planOffset ? this.planOffset < this.totalPlanCount : false
  }

  constructor(
    private readonly planDao: PlanDao,
    private readonly appInsightsService: ApplicationInsightsService,
    private readonly doka360ProjectSelectorService: Doka360ProjectSelectorService
  ) {
    this.doka360ProjectSelectorService.currentDoka360ProjectUid$.subscribe(() => {
      this.planOffset = null
      this.totalPlanCount = undefined
    })
  }

  public async updatePlan(
    plan: Pick<
      Plan,
      'id' | 'name' | 'date' | 'buildingType' | 'stockId' | 'projectId' | 'lastUsed' | 'isCompleted'
    >
  ): Promise<void> {
    if (plan.isCompleted === true) {
      return
    }

    const newPlan = await this.planDao.updatePlan(plan)
    planStore.update(upsertEntities(newPlan))
  }

  public async updateSerializedMesh(plan: Plan, serializedMesh?: string): Promise<void> {
    if (plan.isCompleted === true) {
      return
    }
    if (plan.serializedMesh === serializedMesh) {
      return
    }

    await this.planDao.updateSerializedMesh(plan.id, serializedMesh)
    planStore.update(updateEntities(plan.id, { serializedMesh }))
  }

  public async updateCurrentStep(plan: Plan, currentStep: NavStep): Promise<void> {
    if (plan.isCompleted === true) {
      return
    }
    if (plan.currentStep === currentStep) {
      return
    }

    const newPlan = await this.planDao.updateCurrentStep(plan.id, currentStep)
    planStore.update(updateEntities(plan.id, newPlan))
    if (currentStep !== 'result') {
      this.appInsightsService.addUserEvent(
        ApplicationInsightsStates.PLAN_STEP_CHANGED + ' ' + currentStep,
        plan.id
      )
    }
  }

  public getCurrentStep(planId: number): NavStep | undefined {
    const currentStep = planStore.query(getEntity(planId))?.currentStep
    return currentStep
  }

  public getPlanSerializedMesh$(planId: number): Observable<string | undefined> {
    return planStore.pipe(selectEntity(planId, { pluck: 'serializedMesh' }))
  }

  public async findOne(planId: number): Promise<Plan> {
    let plan = planStore.query(getEntity(planId))

    if (!plan) {
      plan = await this.planDao.findOne(planId)

      if (plan) {
        planStore.update(upsertEntities(plan))
      } else {
        throw new Error('Plan not found: ' + planId)
      }
    }

    // We assume that this call will be triggered on every plan opening to set the current project scope
    this.doka360ProjectSelectorService.setCurrentDoka360ProjectUid(plan.doka360ProjectUid)

    return plan
  }

  public async findOneByEngHubPlanId(engHubPlanUid: string): Promise<Plan> {
    const plan = await this.planDao.findOneByEngHubPlanId(engHubPlanUid)

    if (plan) {
      planStore.update(upsertEntities(plan))
    } else {
      throw new Error('Plan not found with engHubPlanUid: ' + engHubPlanUid)
    }

    // We assume that this call will be triggered on every plan opening to set the current project scope
    this.doka360ProjectSelectorService.setCurrentDoka360ProjectUid(plan.doka360ProjectUid)

    return plan
  }

  public async findOneBySettingsId(settingsId: number): Promise<Plan | undefined> {
    const plans = planStore.query(
      getAllEntitiesApply({
        filterEntity: (e) => e.settingsId === settingsId,
      })
    )

    if (plans && plans.length > 0) {
      return plans[0]
    } else {
      const plan = await this.planDao.findOneBySettingsId(settingsId)

      if (plan) {
        planStore.update(upsertEntities(plan))
      }
      return plan
    }
  }

  public async findAllByProjectId(projectId: number): Promise<Plan[]> {
    const plans = await this.planDao.findAllByProjectId(projectId)
    planStore.update(upsertEntities(plans))
    return plans
  }

  public async findAllByFavouriteId(
    favouriteId: number,
    doka360Uid?: string,
    isCompleted?: boolean
  ): Promise<Plan[]> {
    const plans = await this.planDao.findAllByFavouriteId(favouriteId, doka360Uid, isCompleted)
    planStore.update(upsertEntities(plans))
    return plans
  }

  public async fetchFirstRecentPlans(): Promise<void> {
    const currentDoka360ProjectUid =
      this.doka360ProjectSelectorService.getCurrentDoka360ProjectUid()

    const storePlans = planStore.query(
      getAllEntitiesApply({
        filterEntity: (e) => e.doka360ProjectUid === currentDoka360ProjectUid,
      })
    )

    if (storePlans.length > 0 && this.planOffset !== null && this.planOffset > 0) {
      return
    }

    const doka360ProjectUid = this.doka360ProjectSelectorService.getCurrentDoka360ProjectUid()

    const planListModel = await this.planDao.findAllPlansWith(this.planLimit, 0, doka360ProjectUid)
    const plans = planListModel.plans.map(PlanService.mapPlanModelToPlan)
    planStore.update(upsertEntities(plans))

    this.planOffset = this.planLimit
    this.totalPlanCount = planListModel.totalCount
  }

  public async findNextPlans(): Promise<Plan[]> {
    const doka360ProjectUid = this.doka360ProjectSelectorService.getCurrentDoka360ProjectUid()

    const planListModel = await this.planDao.findAllPlansWith(
      this.planLimit,
      this.planOffset ?? 0,
      doka360ProjectUid
    )
    const plans = planListModel.plans.map(PlanService.mapPlanModelToPlan)
    planStore.update(upsertEntities(plans))

    const currentOffset = this.planOffset ?? 0
    this.planOffset = currentOffset + this.planLimit

    this.totalPlanCount = planListModel.totalCount

    return plans
  }

  public async deleteWithAllRelated(plan: Plan): Promise<void> {
    await this.planDao.deleteWithAllRelated(plan)
    planStore.update(deleteEntities(plan.id))

    this.appInsightsService.addUserEvent(ApplicationInsightsStates.PLAN_DELETED, plan.id)
  }
}
